import { DateTime } from "luxon";

/*
NOTE: This assumes we're only getting an object representing a date WITH NO time value.
For this reason, we force the time zone to be UTC, and in this way we handle issues where
the date is off by a day because of the time zone offset.
*/

const GetISODate = (inputDate) => {
  if (inputDate == null || inputDate == undefined) {
    return inputDate;
  }
  let formattedJsDate = null;

  //NOTE: If the input date is in a format of "12/31/2023", we have to format it differently
  if (isDate(inputDate)) {
    //NOTE: MPI-863 - inputDate is a JS Date object (e.g. from react-datepicker, or a plain
    //"new Date()") whose Y/M/D/H/M/S getters return the intended wall-clock value in the
    //browser's LOCAL timezone. Passing it straight to toISOString() re-interprets those same
    //numbers as a UTC instant conversion, which shifts the calendar date backward for users
    //east of UTC (e.g. UK/BST) when the local time is close to local midnight.
    //Fix: rebuild the date using the local wall-clock components (including time-of-day) but
    //pinned to the UTC zone. This preserves the calendar date for date-only pickers (whose
    //time is already midnight) AND preserves the time-of-day for callers that intentionally
    //pass a real timestamp (e.g. Total Sales' default "now"/"7 days ago" values, which are
    //compared against time-sensitive datetime columns on the backend via BETWEEN clauses -
    //MPI-863 regression). Do NOT truncate to midnight here.
    formattedJsDate = DateTime.fromObject(
      {
        year: inputDate.getFullYear(),
        month: inputDate.getMonth() + 1,
        day: inputDate.getDate(),
        hour: inputDate.getHours(),
        minute: inputDate.getMinutes(),
        second: inputDate.getSeconds(),
        millisecond: inputDate.getMilliseconds(),
      },
      { zone: "utc" }
    ).toJSDate();
  } else if (inputDate.includes("/")) {
    formattedJsDate = DateTime.fromFormat(inputDate, "M/d/yyyy", {
      zone: "utc",
    }).toJSDate();
  }
  //NOTE: If the input date is in a format of "2023-03-03", we have to format it differently
  else if (inputDate.includes("-")) {
    formattedJsDate = DateTime.fromISO(inputDate, { zone: "utc" }).toJSDate();
  }

  if (formattedJsDate) {
    return formattedJsDate.toISOString();
  } else {
    throw new Error("Invalid Date");
  }
};

const GetISODateOnly = (inputDate) => {
  if (inputDate == null || inputDate == undefined) {
    return inputDate;
  }
  let formattedJsDate = null;

  //NOTE: If the input date is in a format of "12/31/2023", we have to format it differently
  if (isDate(inputDate)) {
    //NOTE: MPI-863 - see GetISODate for why we rebuild the date pinned to UTC using its
    //local Y/M/D components instead of passing the JS Date object through unchanged.

    formattedJsDate = DateTime.fromObject(
      {
        year: inputDate.getFullYear(),
        month: inputDate.getMonth() + 1,
        day: inputDate.getDate(),
      },
      { zone: "utc" }
    ).toJSDate();
  } else if (inputDate.includes("/")) {
    formattedJsDate = DateTime.fromFormat(inputDate, "M/d/yyyy", {
      zone: "utc",
    }).toJSDate();
  }
  //NOTE: If the input date is in a format of "2023-03-03", we have to format it differently
  else if (inputDate.includes("-")) {
    formattedJsDate = DateTime.fromISO(inputDate, { zone: "utc" }).toJSDate();
  }

  if (formattedJsDate) {
    return formattedJsDate.toISOString().slice(0, 10);
  } else {
    throw new Error("Invalid Date");
  }
};

const CheckForAbsentTimeSectionAndAddIfNeeded = (inputString) => {
  if (inputString.includes("T")) {
    return inputString;
  } else {
    return inputString + "T00:00:00";
  }
};

const StringToDate = (inputDate) => {
  //This will ensure we are returning a date object - either it is already a date or it will be converted from a string to a date

  //if inputDate is null or undefined return it
  if (inputDate == null || inputDate == undefined) return inputDate;

  return getDate(inputDate);
};

const ShortDateFormat = (inputDate) => {
  //if inputDate is null or undefined return it
  if (inputDate == null || inputDate == undefined) {
    return inputDate;
  }

  const dt = getDate(inputDate);

  return dt.toLocaleDateString("en-US");
};

function ParseTime(inputTime) {
  if (inputTime == "") return null;

  var time = inputTime.match(/(\d+)(:(\d\d))?\s*(p?)/i);
  if (time == null) return null;

  var hours = parseInt(time[1], 10);
  if (hours == 12 && !time[4]) {
    hours = 0;
  } else {
    hours += hours < 12 && time[4] ? 12 : 0;
  }
  var d = new Date();
  d.setHours(hours);
  d.setMinutes(parseInt(time[3], 10) || 0);
  d.setSeconds(0, 0);
  return d;
}

function DateWithoutTime(inputDate) {
  //If argument already a date just return it

  let dt = getDate(inputDate);

  const result = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());

  return result;
}

function convertDateWithSlashesToDateWithDashes(inputDate) {
  if (inputDate.includes("/")) {
    const parts = inputDate.split("/");
    if (parts.length === 3) {
      const month = parts[0].padStart(2, "0");
      const day = parts[1].padStart(2, "0");
      const year = parts[2];
      return `${year}-${month}-${day}`;
    }
  }
  return inputDate;
}

function DateWithoutTimeAsFormattedString(inputDate) {
  if (inputDate.includes("/")) {
    let convertedString = convertDateWithSlashesToDateWithDashes(inputDate);

    return convertedString;
  } else {
    let dt = getDate(inputDate);

    const result =
      new Date(dt).getFullYear() +
      "-" +
      String(new Date(dt).getMonth() + 1).padStart(2, "0") +
      "-" +
      String(new Date(dt).getDate()).padStart(2, "0");

    // console.log(result);

    return result;
  }
}

function IsDefaultDate(inputDate) {
  //retun true if the date is the default date before 1900

  let dt = getDate(inputDate);
  return dt.getFullYear() < 1900;
}

function getDate(inputDate) {
  //If argument already a date just return it
  if (isDate(inputDate)) return inputDate;

  // Handle dates in M/d/yyyy format (e.g., "1/16/2026")
  if (typeof inputDate === "string" && inputDate.includes("/")) {
    try {
      const luxonDate = DateTime.fromFormat(inputDate, "M/d/yyyy", {
        zone: "utc",
      });
      if (luxonDate.isValid) {
        return luxonDate.toJSDate();
      }
    } catch (error) {
      // If Luxon parsing fails, continue with the original logic
    }
  }

  /*NOTE: There is a funny case where there's a difference between 
  '2023-03-03' and '2023-03-03T00:00:00'.
  Basically if we do "new Date('2023-03-03')" it will subtract a day from the display because of a timezone issue
  (the first one is "midnight UTC time" and the second one is "midnight local time").
  Therefore we should check to see if the inputDate contains a "T" to indicate time,
  and if then we should append "T00:00:00" so that it doesn't subtract a day.
  OH, additionally if it's '2023-03-03T00:00:00' IS NOT the same as "2023-03-03T00:00:00Z",
  if the first case it reads it as "midnight according to local time" and in the second case "midnight according to UTC time".
  This makes it so that "2023-03-03" is basically "2023-03-03T00:00:00Z".
  */
  let dt = new Date(CheckForAbsentTimeSectionAndAddIfNeeded(inputDate));

  //If argument string not valid initialize it to default date (year 1900)
  if (isNaN(dt)) dt = new Date(0, 0);

  return dt;
}

function IsDataDateType(input) {
  //if input is null or undefined return false
  if (input == null || input == undefined) return false;

  // Parse the date string into a Date object

  try {
    const date = new Date(input);

    // Check if the date is valid
    return !isNaN(date.getTime());
  } catch (error) {
    //if error on date validation return false automatically
    return false;
  }
}

function isDate(input) {
  return typeof input == typeof new Date();
}

function GetDataWithCleanedDates(sourceData, removeTime = true) {
  //this mwethod will take a list of data and will format all items as date which have a string of date in the key
  //if it is not a date format set the value to null
  //it will also provide option to remove the time part of the date - default removeTime param value is true set to false if you want to keep the time part

  let result = sourceData.map((t) => {
    let cleanedData = { ...t };
    for (let key in cleanedData) {
      if (key.toLowerCase().includes("date")) {
        if (IsDataDateType(cleanedData[key])) {
          cleanedData[key] = removeTime
            ? DateWithoutTime(cleanedData[key])
            : new Date(cleanedData[key]);
        } else {
          cleanedData[key] = null;
        }
      }
    }
    return cleanedData;
  });

  return result;
}

function GetDataWithIsoDateStrings(sourceData, removeTime = true) {
  let result = sourceData.map((t) => {
    let cleanedData = { ...t };
    for (let key in cleanedData) {
      if (key.toLowerCase().includes("date")) {
        if (IsDataDateType(cleanedData[key])) {
          cleanedData[key] = removeTime
            ? DateWithoutTime(cleanedData[key]).toISOString()
            : new Date(cleanedData[key]).toISOString();
        } else {
          cleanedData[key] = null;
        }
      }
    }
    return cleanedData;
  });

  return result;
}

//This function takes a source date and a number of days tro go forward (or back if negative) and returns a new date that is the source date plus the number of days
function GetDateNumberOfDaysFromSource(sourceDate, days) {
  const resultDate = DateWithoutTime(sourceDate);
  resultDate.setDate(resultDate.getDate() + days);
  return resultDate;
}

//This function takes two dates and returns the number of days between them
function GetDaysBetweenDates(date1, date2) {
  const oneDayInMilliseconds = 24 * 60 * 60 * 1000; // Hours * minutes * seconds * milliseconds
  const date1WithoutTime = DateWithoutTime(date1);
  const date2WithoutTime = DateWithoutTime(date2);
  const differenceInMilliseconds = Math.abs(
    date2WithoutTime - date1WithoutTime,
  );
  return Math.round(differenceInMilliseconds / oneDayInMilliseconds);
}

//this method should use same logic as last method except it will a list of columns to process and only apply the date formatting to those columns
function GetDataWithCleanedDatesOnSpecifiedColumns(
  sourceData,
  columnsToPrcess,
  removeTime = true,
) {
  let result = sourceData.map((t) => {
    let cleanedData = { ...t };
    for (let key in cleanedData) {
      if (
        columnsToPrcess
          .map((col) => col.toLowerCase())
          .includes(key.toLowerCase())
      ) {
        if (IsDataDateType(cleanedData[key])) {
          cleanedData[key] = removeTime
            ? DateWithoutTime(cleanedData[key])
            : new Date(cleanedData[key]);
        } else {
          cleanedData[key] = null;
        }
      }
    }
    return cleanedData;
  });

  return result;
}

// Returns true when the check date is within 72 hours of now, meaning the
// elapsed time from the start of the check date day to the current moment
// is less than 72 hours. Used to soft-warn users who submit remittances
// before full processing has had time to complete.
function IsWithin72Hours(checkDate) {
  if (checkDate === null || checkDate === undefined) return false;

  const checkDateStart = DateWithoutTime(checkDate);
  const now = new Date();
  const diffMs = now - checkDateStart;

  // Only trigger for past/same-day dates (diffMs >= 0); future dates are
  // handled separately by the "cannot be in the future" validation rule.
  if (diffMs < 0) return false;

  const seventyTwoHoursMs = 72 * 60 * 60 * 1000;
  return diffMs < seventyTwoHoursMs;
}

//function that take two datetimes and returns the number of seconds between them
function GetSecondsBetweenDates(date1, date2) {
  const dt1 = DateTime.fromJSDate(date1);
  const dt2 = DateTime.fromJSDate(date2);
  const differenceInSeconds = Math.abs(dt2.diff(dt1, "seconds").seconds);
  return Math.round(differenceInSeconds);
}

const GetISODateTimeLocal = (inputDate) => {
  if (inputDate == null || inputDate == undefined) return inputDate;
  const dt = isDate(inputDate) ? inputDate : getDate(inputDate);
  return DateTime.fromJSDate(dt).toISO({ includeOffset: false });
};

function GetUtcMidnightDate(inputDate) {
  // Return null or undefined if input is null or undefined
  if (inputDate == null || inputDate == undefined) {
    return inputDate;
  }

  // Convert input to a Date object if it's not already
  const dt = getDate(inputDate);

  // Create a new Date object with the same year, month, and day, but with time set to 00:00:00 UTC
  const utcDate = new Date(
    Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate(), 0, 0, 0, 0),
  );

  return utcDate;
}

export {
  GetISODate,
  GetISODateTimeLocal,
  GetISODateOnly,
  StringToDate,
  ShortDateFormat,
  ParseTime,
  DateWithoutTime,
  DateWithoutTimeAsFormattedString,
  IsDefaultDate,
  IsDataDateType,
  IsWithin72Hours,
  GetDataWithCleanedDates,
  GetDataWithCleanedDatesOnSpecifiedColumns,
  GetDateNumberOfDaysFromSource,
  GetDaysBetweenDates,
  GetSecondsBetweenDates,
  GetDataWithIsoDateStrings,
  GetUtcMidnightDate,
};
